import React, { useMemo, useCallback } from 'react';

import { SettingsModelTypeCourse } from '../../../../../interfaces/merchants/resources/books';
import {
  goTo,
  nextStep,
  finalStep,
} from '../../../../../redux/actions/merchants/resources/books';

import {
  StepItems,
  BookStepSettingPair,
} from '../../../../../molecules/merchant/resources/book/BooksStep';
import InputBookingDateTime from '../InputBookingDateTime';
import InputBookingTarget from '../InputBookingTarget';
import InputCompletion from '../InputCompletion';
import InputCompletionFailed from '../InputCompletionFailed';
import InputConfirmation from '../InputConfirmation';
import InputUserInfo from '../InputUserInfo';
import ReservationFlowAuthPage from '../ReservationFlowAuthPage';
import BookingWithPageDetail from '../BookingWithPageDetail';
import { InputPaymentOrganism } from '../InputPayment';

import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooks } from '../../../../../redux/selectors/merchants/resources/books';
import { useIsRequirePayment } from '../../../../../hooks/booking-page';
import { BooksStepOrganism } from '../../../../reservation-flow/BooksStep';
import { useAppDispatch } from '../../../../../redux';

interface Props {
  isExpressFlow: boolean;
  merchantId: string;
  resourcePublicId: string;
  lineLiffId: string | undefined;
}

const FULL_WORKFLOW_STEPS = [
  StepItems.Booking,
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Payment,
  StepItems.Confirmation,
];
const SKIP_PAYMENT_WORKFLOW_STEPS = [
  StepItems.Booking,
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Confirmation,
];

const ERROR_REINPUT_PAYMENT_STEP = 97;
const ERROR_RESTART_STEP = 98;
const ERROR_RESELECT_STEP = 99;

const ModelTypeCourse: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();

  const merchantResourcesBooksState = useShallowEqualSelector(
    (state: ReduxState) => getMerchantResourcesBooks(state),
  );
  const { merchantResource, step, postable, renew } =
    merchantResourcesBooksState;
  const settings = useMemo<SettingsModelTypeCourse>(
    () => merchantResourcesBooksState.settings as SettingsModelTypeCourse,
    [merchantResourcesBooksState],
  );
  const publicId = useMemo(
    () => merchantResourcesBooksState.merchantId!,
    [merchantResourcesBooksState],
  );

  const renderRequireLoginAt = useCallback(
    (step: number) => {
      return (
        <ReservationFlowAuthPage
          moveToNext={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          renew={renew}
          resourcePublicId={props.resourcePublicId}
        />
      );
    },
    [dispatch, renew, props.resourcePublicId],
  );

  const renderInputBookingTargetAt = useCallback(
    (step: number) => {
      if (props.isExpressFlow) {
        return (
          <BookingWithPageDetail
            merchantId={props.merchantId}
            merchantResource={merchantResource!}
            showPerformableStaffMembers={false}
          >
            <InputBookingTarget
              modelType={settings.modelType}
              settings={settings.bookingTargets}
              postable={postable!}
              onSubmit={() => dispatch(nextStep(step))}
              onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
              renew={renew}
              withExpressFlow={props.isExpressFlow}
              resourcePublicId={props.resourcePublicId}
            />
          </BookingWithPageDetail>
        );
      } else {
        return (
          <InputBookingTarget
            modelType={settings.modelType}
            settings={settings.bookingTargets}
            postable={postable!}
            onSubmit={() => dispatch(nextStep(step))}
            onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
            renew={renew}
            withExpressFlow={props.isExpressFlow}
            resourcePublicId={props.resourcePublicId}
          />
        );
      }
    },
    [dispatch, props, merchantResource, settings, postable, renew],
  );

  const renderInputBookingDateTimeAt = useCallback(
    (step: number, modifierStep: number) => {
      return (
        <InputBookingDateTime
          settings={settings.bookingDateTimes}
          postable={postable!}
          onSubmit={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          backToModifyPage={() => dispatch(goTo(modifierStep))}
          resourcePublicId={props.resourcePublicId}
        />
      );
    },
    [dispatch, props, settings, postable],
  );

  const renderInputUserInfoAt = useCallback(
    (step: number, submitLabelIntlId: string) => {
      return (
        <InputUserInfo
          settings={settings.userInfo}
          onSubmit={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          submitLabelIntlId={submitLabelIntlId}
          merchantPublicId={props.merchantId}
          resourcePublicId={props.resourcePublicId}
        />
      );
    },
    [dispatch, settings, props],
  );

  const renderInputPaymentAt = useCallback(
    (step: number) => {
      return (
        <InputPaymentOrganism
          settings={settings.payment!}
          merchantPublicId={publicId}
          postable={postable!}
          onSubmit={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
        />
      );
    },
    [dispatch, settings, publicId, postable],
  );

  const renderInputConfirmationAt = useCallback(
    (step: number) => {
      return (
        <InputConfirmation
          merchantResource={{
            publicId: merchantResource!.publicId,
            name: merchantResource!.name,
            imageUrl: merchantResource!.primaryImage?.thumb?.url,
            modelType: merchantResource!.modelType,
          }}
          settings={settings.confirmation}
          postable={postable!}
          onSubmit={() => dispatch(finalStep(step))}
          onReinputPayment={() => dispatch(goTo(ERROR_REINPUT_PAYMENT_STEP))}
          onReselect={() => dispatch(goTo(ERROR_RESELECT_STEP))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          publicId={publicId}
          lineLiffId={props.lineLiffId}
        />
      );
    },
    [
      dispatch,
      settings,
      postable,
      publicId,
      merchantResource,
      props.lineLiffId,
    ],
  );

  const renderInputCompletion = useCallback(() => {
    return (
      <InputCompletion
        merchantResource={{
          publicId: merchantResource!.publicId,
          name: merchantResource!.name,
          imageUrl: merchantResource!.primaryImage?.thumb?.url,
          modelType: merchantResource!.modelType,
        }}
        settings={settings.completion!}
        publicId={publicId}
        withExpressFlow={props.isExpressFlow}
        resourcePublicId={props.resourcePublicId}
      />
    );
  }, [props, merchantResource, settings, publicId]);

  const renderFailedRestart = useCallback(() => {
    return (
      <InputCompletionFailed
        settings={settings.completion!}
        backToLabel={'books.submitLabel.restart'}
        backToModifyPage={() => location.reload()}
      />
    );
  }, [settings]);

  const renderFailedReselect = useCallback(
    (modifierStep: number) => {
      return (
        <InputCompletionFailed
          settings={settings.completion!}
          backToLabel={'books.submitLabel.reselect'}
          backToModifyPage={() => dispatch(goTo(modifierStep))}
        />
      );
    },
    [dispatch, settings],
  );

  const renderFailedReinput = useCallback(
    (modifierStep: number) => {
      return (
        <InputCompletionFailed
          settings={settings.completion!}
          backToLabel={'books.submitLabel.reinput'}
          backToModifyPage={() => dispatch(goTo(modifierStep))}
        />
      );
    },
    [dispatch, settings],
  );

  const fullWorkFlowSetting = useMemo<BookStepSettingPair>(() => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.Booking,      renderFn: () => renderInputBookingTargetAt(1)   },
      2: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => renderInputBookingDateTimeAt(2, 1) },
      3: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => renderInputUserInfoAt(3, 'books.submitLabel.payment') },
      4: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => renderInputPaymentAt(4)         },
      5: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderInputConfirmationAt(5)    },
      6: { displayProgress: false, currentProgress: null,                   renderFn: () => renderInputCompletion()         },

      [ERROR_REINPUT_PAYMENT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReinput(4) },
      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReselect(2) },
    }[step!];

    return [FULL_WORKFLOW_STEPS, setting];
  }, [
    step,
    renderRequireLoginAt,
    renderInputBookingTargetAt,
    renderInputBookingDateTimeAt,
    renderInputUserInfoAt,
    renderInputPaymentAt,
    renderInputConfirmationAt,
    renderInputCompletion,
    renderFailedReinput,
    renderFailedRestart,
    renderFailedReselect,
  ]);

  const skipPaymentFlowSetting = useMemo<BookStepSettingPair>(() => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.Booking,      renderFn: () => renderInputBookingTargetAt(1)   },
      2: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => renderInputBookingDateTimeAt(2, 1) },
      3: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => renderInputUserInfoAt(3, 'books.submitLabel.confirm') },
      4: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderInputConfirmationAt(4)    },
      5: { displayProgress: false, currentProgress: null,                   renderFn: () => renderInputCompletion()         },

      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReselect(2) },
    }[step!];

    return [SKIP_PAYMENT_WORKFLOW_STEPS, setting];
  }, [
    step,
    renderRequireLoginAt,
    renderInputBookingTargetAt,
    renderInputBookingDateTimeAt,
    renderInputUserInfoAt,
    renderInputConfirmationAt,
    renderInputCompletion,
    renderFailedRestart,
    renderFailedReselect,
  ]);

  const isRequirePayment = useIsRequirePayment();

  const [allSteps, setting] = useMemo(() => {
    return isRequirePayment ? fullWorkFlowSetting : skipPaymentFlowSetting;
  }, [fullWorkFlowSetting, skipPaymentFlowSetting, isRequirePayment]);

  return (
    <>
      {setting && setting.currentProgress && setting.displayProgress && (
        <BooksStepOrganism
          allSteps={allSteps}
          current={allSteps.indexOf(setting.currentProgress) + 1}
          resourcePublicId={merchantResource?.publicId ?? ''}
        />
      )}
      {setting && setting.renderFn()}
    </>
  );
};

export default ModelTypeCourse;
